import { dataPersistence } from './dataPersistence'
import { wizardApi } from './api/wizard'

const USE_DB = typeof import.meta !== 'undefined' && import.meta.env?.VITE_USE_DB_WIZARD !== 'false'

/**
 * Clear all localStorage keys related to exchange/wizard data.
 * Kept as a safety net alongside DB reset.
 */
function clearLocalStorage() {
  sessionStorage.removeItem('sales_hub_shown_demo1_session')
  localStorage.removeItem('sales_hub_shown_demo1_session')

  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (
      key.startsWith('exchange_wizard_') ||
      key.startsWith('sale_wizard_') ||
      key.startsWith('user_data_cache') ||
      key.startsWith('wizard_state_') ||
      key.startsWith('document_') ||
      key.startsWith('esign_') ||
      key.startsWith('exchange_agreement') ||
      key.startsWith('user_saved_signature') ||
      key.startsWith('exchange_docs_') ||
      key.startsWith('user_activities') ||
      key.startsWith('property_sale_date') ||
      key.startsWith('calculator_savings') ||
      key.startsWith('calculator_results') ||
      key.startsWith('open1031:calculator:') ||
      key.startsWith('open1031:docs:') ||
      key.startsWith('demo_activities_logged') ||
      key.startsWith('sales_hub_submitted') ||
      key.startsWith('identification_hub_submitted') ||
      key.startsWith('purchase_hub_submitted') ||
      key.startsWith('disbursement_hub_submitted') ||
      key.startsWith('acquisition_hub_submitted') ||
      key.startsWith('escrow_hub_submitted') ||
      key.startsWith('irs_hub_submitted') ||
      key.startsWith('hub_locked_') ||
      key.startsWith('hub_status_') ||
      key.startsWith('escrow_data_') ||
      key.startsWith('pending_sync_') ||
      key.startsWith('w9_') ||
      key.startsWith('kyc_modal_shown_') ||
      key.startsWith('sales_hub_phase1_step_') ||
      key.startsWith('identification_hud1_') ||
      key.startsWith('identification_step_') ||
      key.startsWith('identification_settlement') ||
      key.startsWith('hub_doc_status_') ||
      key.includes('_initial') ||
      key.includes('_phase1') ||
      key.includes('_exchange') ||
      key.includes('_acquisition') ||
      key.includes('_signed') ||
      key.includes('_signature') ||
      key.includes('_confirmed')
    )) {
      keysToRemove.push(key)
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key))

  dataPersistence.clear()

  // Explicit cleanup for keys that patterns might miss
  const explicitKeys = [
    'demo_unlock_all', 'demo_identity_verified',
    'hub_locked_sales', 'hub_locked_identification', 'hub_locked_purchase', 'hub_locked_disbursement', 'hub_locked_irs',
    'sales_hub_submitted_date', 'identification_hub_submitted_date', 'purchase_hub_submitted_date', 'disbursement_hub_submitted_date', 'acquisition_hub_submitted_date', 'escrow_hub_submitted_date', 'irs_hub_submitted_date',
    'escrow_hub_submitted', 'irs_hub_submitted',
    'exchange_agreement_signed', 'exchange_agreement_data', 'exchange_agreement_signed_date', 'exchange_agreement_signed_by',
    'w9_signed', 'w9_signature', 'w9_signed_date', 'w9_signed_by',
    'sales_hub_submitted', 'calculator_results', 'calculator_savings',
    'identification_hud1_uploaded', 'identification_hud1_extracted', 'identification_form_signed',
    'identification_form_signature', 'identification_form_signed_date', 'identification_form_signed_by',
    'disbursement_auth_signed', 'disbursement_auth_signature', 'disbursement_auth_signed_date', 'disbursement_auth_signed_by',
  ]
  explicitKeys.forEach(key => localStorage.removeItem(key))

  localStorage.setItem('demo_reset_just_completed', 'true')

  return keysToRemove.length
}

export const demoReset = {
  resetUser: async (userId: string) => {
    try {
      // 1. Reset DB data
      if (USE_DB) {
        const exchangeId = userId || 'anon'
        await wizardApi.resetDemo(exchangeId)
          .catch(err => console.warn('DB reset failed (continuing with localStorage):', err))
      }

      // 2. Clear localStorage
      const cleared = clearLocalStorage()

      return { success: true, cleared }
    } catch (error) {
      console.error('Demo reset failed:', error)
      throw error
    }
  },

  resetAll: () => {
    try {
      // DB reset is async — fire and forget for resetAll (sync method)
      // The next login will trigger server-side seed if no data exists
      if (USE_DB) {
        // We don't have exchangeId here, so just clear localStorage
        // The API reset endpoint requires an exchangeId
      }

      const cleared = clearLocalStorage()
      return { success: true, cleared }
    } catch (error) {
      console.error('Demo reset failed:', error)
      throw error
    }
  }
}
